import React, { useState } from 'react';
import { Building2, Key, Users, FileText, Shield, Clock, ExternalLink, LogIn, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Votre message a été envoyé avec succès !');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md border-b border-gold/20 shadow-xl !bg-[rgba(10,42,74,0.95)]">
        <div className="container mx-auto px-6 py-5 !bg-[#0A2A4A]">
          <div className="flex items-center justify-between !bg-[rgba(10,42,74,0)]">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_residence-hub-32/artifacts/he8dmeuv_Design%20sans%20titre%20%283%29.png"
                alt="La Clé de la Copro"
                className="h-14 w-14 object-contain flex-shrink-0" />

              <div className="flex flex-col justify-center">
                <h1 className="text-xl font-bold text-gold leading-tight">LA CLÉ DE LA COPRO</h1>
                <p className="!text-sm !text-[#FFFFFF]">Syndic de copropriété</p>
              </div>
            </div>
            <nav className="hidden lg:flex items-center gap-6">
              <a href="#accueil" className="text-slate-300 hover:text-gold font-medium text-sm">Accueil</a>
              <a href="#services" className="text-slate-300 hover:text-gold font-medium text-sm">Services</a>
              <a href="#contact" className="text-slate-300 hover:text-gold font-medium text-sm">Contact</a>
              <div className="flex items-center gap-3 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gold/40 text-gold hover:bg-gold/10 hover:border-gold"
                  onClick={() => window.open('https://extranet2.ics.fr/V5/connexion-cledelacopro.html', '_blank')}>

                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  Extranet
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="accueil" className="relative py-32 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-full px-5 py-2 mb-8 backdrop-blur-sm">
              <Key className="h-4 w-4 text-gold" />
              <span className="text-gold font-semibold text-sm">Votre partenaire en gestion de copropriété</span>
            </div>
            
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              La gestion de votre copropriété
              <span className="block text-gold mt-2">en toute confiance</span>
            </h2>
            
            <p className="text-xl text-slate-300 mb-12 leading-relaxed max-w-3xl mx-auto">
              Expertise, transparence et proximité pour une gestion optimale de votre immeuble. 
              Nous mettons notre savoir-faire au service de votre patrimoine.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Button
                size="lg"
                className="bg-gold text-slate-900 hover:bg-gold/90 font-bold text-lg px-10 py-7 shadow-2xl hover:shadow-gold/20 hover:scale-105"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>

                Nous contacter
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-slate-400 text-white hover:bg-white/10 hover:border-gold text-lg px-10 py-7"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}>

                Découvrir nos services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <div className="inline-block bg-gold/10 border border-gold/30 rounded-full px-5 py-2 mb-6">
              <span className="text-gold font-semibold text-sm">Nos Services</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Une gamme complète de services
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Nous accompagnons les copropriétaires dans la gestion quotidienne et stratégique de leur patrimoine
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Service 1 */}
            <Card className="group bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-gold/50 hover:shadow-2xl hover:shadow-gold/10 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold/30 group-hover:scale-110">
                  <Building2 className="h-8 w-8 text-gold" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Gestion Administrative</h3>
                <p className="text-slate-400 leading-relaxed">
                  Convocations, procès-verbaux, suivi des décisions d'assemblée générale et gestion documentaire complète.
                </p>
              </CardContent>
            </Card>

            {/* Service 2 */}
            <Card className="group bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-gold/50 hover:shadow-2xl hover:shadow-gold/10 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold/30 group-hover:scale-110">
                  <FileText className="h-8 w-8 text-gold" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Gestion Comptable</h3>
                <p className="text-slate-400 leading-relaxed">
                  Tenue des comptes, budgets prévisionnels, appels de fonds et états financiers détaillés.
                </p>
              </CardContent>
            </Card>

            {/* Service 3 */}
            <Card className="group bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-gold/50 hover:shadow-2xl hover:shadow-gold/10 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold/30 group-hover:scale-110">
                  <Users className="h-8 w-8 text-gold" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Conseil & Assistance</h3>
                <p className="text-slate-400 leading-relaxed">
                  Accompagnement personnalisé, conseil juridique et assistance aux copropriétaires et au conseil syndical.
                </p>
              </CardContent>
            </Card>

            {/* Service 4 */}
            <Card className="group bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-gold/50 hover:shadow-2xl hover:shadow-gold/10 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold/30 group-hover:scale-110">
                  <Shield className="h-8 w-8 text-gold" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Gestion Technique</h3>
                <p className="text-slate-400 leading-relaxed">
                  Suivi des travaux, entretien de l'immeuble, relation avec les prestataires et contrôle qualité.
                </p>
              </CardContent>
            </Card>

            {/* Service 5 */}
            <Card className="group bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-gold/50 hover:shadow-2xl hover:shadow-gold/10 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold/30 group-hover:scale-110">
                  <Clock className="h-8 w-8 text-gold" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Disponibilité</h3>
                <p className="text-slate-400 leading-relaxed">
                  Une équipe à votre écoute, réactive et disponible pour répondre à toutes vos questions.
                </p>
              </CardContent>
            </Card>

            {/* Service 6 */}
            <Card className="group bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-gold/50 hover:shadow-2xl hover:shadow-gold/10 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold/30 group-hover:scale-110">
                  <Key className="h-8 w-8 text-gold" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Plateforme Digitale</h3>
                <p className="text-slate-400 leading-relaxed">
                  Accès 24/7 à votre espace personnel, documents en ligne et suivi en temps réel de votre copropriété.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block bg-gold/10 border border-gold/30 rounded-full px-5 py-2 mb-6">
                <span className="text-gold font-semibold text-sm">Contactez-nous</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Une question ? Parlons-en !
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Notre équipe est à votre disposition pour répondre à toutes vos demandes
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {/* Contact Info */}
              <div className="md:col-span-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-10">
                <h3 className="text-2xl font-bold text-white mb-8">Informations</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1 text-sm">Adresse</p>
                      <p className="text-slate-400 text-sm">9 cité curiale  - Residence les Jasmins Apt.15
97470 SAINT BENOIT</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1 text-sm">Horaires</p>
                      <p className="text-slate-400 text-sm">Lun - Ven : 8h - 12 et 13h - 18h
Samedi : 8h - 12h</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1 text-sm">Email</p>
                      <p className="text-slate-400 text-sm">syndic@lacledelacopro.re</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1 text-sm">Téléphone</p>
                      <p className="text-slate-400 text-sm">06.92.14.41.00</p>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-700">
                    <p className="text-sm text-slate-400 mb-4">Accès rapides :</p>
                    <div className="flex flex-col gap-3">
                      <Button variant="outline" size="sm"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700/50 hover:text-gold justify-start"
                      onClick={() => window.open('https://extranet2.ics.fr/V5/connexion-cledelacopro.html', '_blank')}>

                        <ExternalLink className="h-4 w-4 mr-2" />
                        Accéder à l'Extranet
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="md:col-span-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Nom complet *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-gold focus:ring-gold" />

                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre.email@exemple.com"
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-gold focus:ring-gold" />

                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Téléphone *
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="06 12 34 56 78"
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-gold focus:ring-gold" />

                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Décrivez votre demande..."
                      className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-gold focus:ring-gold resize-none" />

                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold text-slate-900 hover:bg-gold/90 font-bold text-base py-6 shadow-xl hover:shadow-gold/20">

                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900/95 border-t border-slate-800 py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_residence-hub-32/artifacts/he8dmeuv_Design%20sans%20titre%20%283%29.png"
                  alt="La Clé de la Copro"
                  className="h-12 w-12 object-contain" />

                <div>
                  <h3 className="text-lg font-bold text-gold">LA CLÉ DE LA COPRO</h3>
                  <p className="text-xs text-slate-400">Syndic de copropriété</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Expert en gestion de copropriété, nous mettons notre savoir-faire au service de votre patrimoine.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 text-gold">Liens rapides</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#accueil" className="text-slate-400 hover:text-gold text-sm block">
                    Accueil
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-slate-400 hover:text-gold text-sm block">
                    Nos Services
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-slate-400 hover:text-gold text-sm block">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 text-gold">Espace Client</h4>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-gold justify-start"
                  onClick={() => window.open('https://extranet2.ics.fr/V5/connexion-cledelacopro.html', '_blank')}>

                  <ExternalLink className="h-4 w-4 mr-2" />
                  Extranet
                </Button>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} La Clé de la Copro. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;